import {
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import * as Sentry from '@sentry/react';
import { jwtDecode } from 'jwt-decode';
import store from '../store/indexRedux';
import { setAccessToken } from '../store/authv2Slice';
import { isTokenExpired } from '../components/utils/cognito';
import { SessionExpiredError } from './sessionExpiredError';

/**
 * Fresh-access-token helper for long-lived SPA tabs.
 *
 * App.tsx copies the Cognito access token into Redux once at load; the login client's access
 * tokens live 24h (AccessTokenValidity 1 day), so a tab left open past that sends a dead token and the portals backend answers
 * 401 "Invalid token". This helper reads the `session=` cookie (set by the external login
 * app) on every call and, when the access token is expired or about to be, refreshes it with
 * the cookie's refresh token and writes the new session back into the same cookie.
 *
 * Deliberately does NOT use getSessionFromCookie(): that function calls logout() — a hard
 * redirect — as soon as the cookie's ID token is expired, which is exactly the state this
 * helper exists to recover from.
 */

const COGNITO_USER_POOL_ID = 'us-east-1_1epcyobz1';

/**
 * App client of the LOGIN APP (yuso-auth-frontend src/utils/cognito.ts), NOT the
 * `2haiep5j3460kffbvv3mfhbvib` client in src/api/user.ts. The refresh token in the `session=`
 * cookie was issued to the login app's client, and Cognito REFRESH_TOKEN_AUTH only accepts a
 * refresh token under the client that issued it. Keep this in sync with the login app.
 */
export const LOGIN_APP_COGNITO_CLIENT_ID = '6n3usn7srqml60sl0rgeb2vhgm';

/** Refresh this many seconds before `exp`, so a token never expires in flight. */
const REFRESH_SKEW_SECONDS = 60;

interface SessionCookie {
  IdToken: string;
  AccessToken: string;
  RefreshToken: string;
}

const findCookie = (name: string): string | undefined =>
  document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.slice(name.length + 1);

/** Side-effect-free read of the login app's `session=` cookie. */
const readSessionCookie = (): SessionCookie | null => {
  const raw = findCookie('session');
  if (!raw) return null;
  try {
    const data = JSON.parse(raw) as Partial<SessionCookie> | null;
    if (!data || typeof data.AccessToken !== 'string' || data.AccessToken === '') return null;
    return {
      IdToken: typeof data.IdToken === 'string' ? data.IdToken : '',
      AccessToken: data.AccessToken,
      RefreshToken: typeof data.RefreshToken === 'string' ? data.RefreshToken : '',
    };
  } catch {
    return null;
  }
};

/** Access token from the WorkOS login app's `workos_session` cookie, or null if absent. */
const readWorkOSAccessToken = (): string | null => {
  const raw = findCookie('workos_session');
  if (!raw) return null;
  try {
    const data = JSON.parse(decodeURIComponent(raw)) as { AccessToken?: unknown } | null;
    return typeof data?.AccessToken === 'string' && data.AccessToken !== '' ? data.AccessToken : null;
  } catch {
    return null;
  }
};

/**
 * Writes the session back exactly as the login app does (yuso-auth-frontend
 * src/utils/cognito.ts): raw JSON (every reader does JSON.parse on the cookie value), key
 * order IdToken, AccessToken, RefreshToken, no `expires` (browser-session cookie), and the
 * same path/domain so logout() — which expires `session=` on that path/domain — still
 * clears it.
 */
const writeSessionCookie = ({ IdToken, AccessToken, RefreshToken }: SessionCookie): void => {
  const isLocalhost = window.location.hostname === 'localhost';
  const cookieOptions = isLocalhost
    ? 'path=/; domain=localhost; SameSite=None; Secure;'
    : 'path=/; domain=.yuso.io; SameSite=None; Secure;';
  document.cookie = `session=${JSON.stringify({ IdToken, AccessToken, RefreshToken })}; ${cookieOptions}`;
};

const decodeClaims = (token: string): Record<string, unknown> => {
  try {
    return jwtDecode<Record<string, unknown>>(token);
  } catch {
    return {};
  }
};

const asNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

/** `cognito:username` from the ID token, else the access token's `username`, else `sub`. */
const resolveUsername = (session: SessionCookie): string | undefined => {
  const id = decodeClaims(session.IdToken);
  const access = decodeClaims(session.AccessToken);
  return (
    asNonEmptyString(id['cognito:username']) ??
    asNonEmptyString(access.username) ??
    asNonEmptyString(id.sub) ??
    asNonEmptyString(access.sub)
  );
};

const refreshCognitoSession = (session: SessionCookie): Promise<SessionCookie> =>
  new Promise((resolve, reject) => {
    const username = resolveUsername(session);
    if (!username || !session.RefreshToken) {
      reject(new SessionExpiredError());
      return;
    }
    const pool = new CognitoUserPool({
      UserPoolId: COGNITO_USER_POOL_ID,
      ClientId: LOGIN_APP_COGNITO_CLIENT_ID,
    });
    const user = new CognitoUser({ Username: username, Pool: pool });
    user.refreshSession(
      new CognitoRefreshToken({ RefreshToken: session.RefreshToken }),
      (err: unknown, refreshed: CognitoUserSession | null) => {
        if (err || !refreshed) {
          reject(err ?? new Error('Cognito refresh returned no session'));
          return;
        }
        resolve({
          IdToken: refreshed.getIdToken().getJwtToken(),
          AccessToken: refreshed.getAccessToken().getJwtToken(),
          // Cognito normally does not rotate the refresh token (getToken() is empty) —
          // keep the one we already have in that case.
          RefreshToken: refreshed.getRefreshToken()?.getToken() || session.RefreshToken,
        });
      }
    );
  });

/** Keeps the Redux copy (authv2.accessToken) in step with the token we actually send. */
const syncReduxToken = (token: string): void => {
  if (store.getState().authv2.accessToken !== token) {
    store.dispatch(setAccessToken(token));
  }
};

let inFlightRefresh: Promise<string> | null = null;

const refreshAndPersist = (session: SessionCookie): Promise<string> => {
  if (!inFlightRefresh) {
    inFlightRefresh = (async () => {
      try {
        const refreshed = await refreshCognitoSession(session);
        writeSessionCookie(refreshed);
        syncReduxToken(refreshed.AccessToken);
        return refreshed.AccessToken;
      } catch (err) {
        if (!(err instanceof SessionExpiredError)) {
          // A failed refresh is otherwise invisible (the user only sees "session expired"),
          // so report the Cognito error to Sentry for support.
          Sentry.captureException(err, {
            tags: { feature: 'fluviusDataManager', component: 'authToken', type: 'cognitoRefresh' },
          });
        }
        throw new SessionExpiredError();
      } finally {
        inFlightRefresh = null;
      }
    })();
  }
  return inFlightRefresh;
};

/**
 * Returns an access token that is valid for at least REFRESH_SKEW_SECONDS, refreshing the
 * Cognito session first when needed (or always, with `isForced`). Concurrent callers share a
 * single refresh. Throws SessionExpiredError when no valid token can be produced.
 */
export const getFreshAccessToken = async ({ isForced = false }: { isForced?: boolean } = {}): Promise<string> => {
  // WorkOS login: `workos_session` holds only an access token (no refresh token), so it is
  // used until it expires; an expired or rejected token means the user must log in again.
  const workosToken = readWorkOSAccessToken();
  if (workosToken !== null) {
    if (isForced || isTokenExpired(workosToken, REFRESH_SKEW_SECONDS)) throw new SessionExpiredError();
    syncReduxToken(workosToken);
    return workosToken;
  }

  // Google login: the login app stores a bare access token plus its expiry and no refresh
  // token, so it can be used until it expires but never refreshed.
  const googleToken = findCookie('google_access_token');
  if (googleToken !== undefined) {
    if (isForced || googleToken === '') throw new SessionExpiredError();
    const expiresAt = parseInt(findCookie('google_token_expires_in') ?? '', 10);
    if (!Number.isNaN(expiresAt) && Date.now() >= expiresAt) throw new SessionExpiredError();
    return googleToken;
  }

  const session = readSessionCookie();
  if (!session) throw new SessionExpiredError();

  const needsRefresh =
    isForced ||
    isTokenExpired(session.AccessToken, REFRESH_SKEW_SECONDS) ||
    // The ID token is refreshed alongside: other pages log the user out as soon as the
    // cookie's ID token is expired (getSessionFromCookie), so keep it alive too.
    (session.IdToken !== '' && isTokenExpired(session.IdToken, REFRESH_SKEW_SECONDS));

  if (!needsRefresh) {
    syncReduxToken(session.AccessToken);
    return session.AccessToken;
  }

  return refreshAndPersist(session);
};
